import { useEffect, useMemo, useRef, useState } from "react";
import { Workspace } from "../components/Shell";
import { VideoThumb } from "../components/VideoThumb";
import { Icon } from "../components/Icon";
import { href } from "../lib/route";
import { shortDate } from "../lib/weekLine";
import { acceptExerciseName, clampWorkoutName } from "../lib/workoutNameCap";
import {
  addDay, addDays, addExercise, addSet, moveDay, readWeek, removeDay, removeExercise, removeSet, renameDay, setCell, setNotes, swapExercise,
  weekChanges, WEEKDAYS, weekdayOf, type ExercisePath, type PickedMovement, type SetField,
} from "../lib/reviewerWeek";
import type { WeekExercise, WeekTree } from "../lib/reviewerWeekVocabulary.generated";
import { nameOr, PROPOSAL_SENT_FINAL, PROPOSAL_WORDS } from "../lib/reviewerSeat";
import { useReviewerLinks, useReviewerWeek } from "./useReviewer";
import { useMyLibraryNames } from "./useMyLibraryNames";
import { saveProposal } from "./reviewerWrite";

/* THE BUILDER IN REVIEWER MODE — `#/reviewing/<clientId>/week/<monday>` (reviewer seat step 2, 2026-10-10; brief § Week 8b,
 * his words: *"he might change my whole program too, who knows?"*). William edits a COPY of the written week in memory —
 * days moved, renamed, removed or added; exercises swapped, added, removed; every set's numbers; a cue — and saves it as ONE
 * `reviewer_week_proposals` row (draft, then Send = final). The client sees it as week 8b beside the written week (step 3).
 *
 * ⚠️ A PAGE OF ITS OWN, NOT A MODE OF `WorkoutBuilder`, ON PURPOSE: the coach's builder saves through `executeSave`, which
 * writes `coach_workouts` / `coach_workout_*` — a reviewer mode inside it would share the one code path the seat must never
 * reach. Here every edit is a pure function over the tree (`reviewerWeek.ts`, the ONE generated vocabulary) and the only
 * write is the proposal (`reviewerWrite.saveProposal`). The ruler `reviewerSeatNoCoachWrite.test.ts` reads this file.
 * ⚠️ NO DEMOS: the seat's reads carry no library media (see `ReviewerReview`) — the square is the "no video" mark. */
export function ReviewerWeek({ clientId, monday }: { clientId: string; monday: string }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus(); }, [monday]);
  const links = useReviewerLinks();
  const link = links.state.status === "ready" ? links.state.data.find((l) => l.client_id === clientId && l.role === "reviewer" && l.status === "active") : undefined;
  const clientName = nameOr(link?.other_name);
  const { state, reload } = useReviewerWeek(clientId, monday);
  const data = state.status === "ready" ? state.data : null;
  const library = useMyLibraryNames();

  const [copy, setCopy] = useState<WeekTree | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<"save" | "send" | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  // Seeded ONCE per load: his saved proposal if there is one (and it reads), else the written week.
  useEffect(() => {
    if (data === null) return;
    const saved = data.proposal === null ? null : readWeek(data.proposal.week, monday);
    setCopy(saved ?? data.written);
    setNote(data.proposal?.note ?? "");
    setDirty(false);
  }, [data, monday]);
  const sent = data?.proposal?.sent_at != null;
  const edit = (f: (w: WeekTree) => WeekTree) => { setCopy((w) => (w === null ? w : f(w))); setDirty(true); setOutcome(null); };
  const changes = useMemo(() => (data === null || copy === null ? [] : weekChanges(data.written, copy)), [data, copy]);

  const write = (send: boolean) => {
    if (copy === null) return;
    setBusy(send ? "send" : "save"); setConfirming(false); setOutcome(null);
    void saveProposal({ clientId, monday, proposalId: data?.proposal?.id ?? null, week: copy, note, send }).then((r) => {
      setBusy(null);
      if (r.error !== null) { setOutcome(`Not saved: ${r.error}`); return; }
      setOutcome(send ? null : "Draft saved.");
      reload();
    });
  };

  const dates = WEEKDAYS.map((_, i) => addDays(monday, i));
  const [addOn, setAddOn] = useState(dates[0] ?? monday);

  return (
    <Workspace>
      <main id="main" className="review-page ours-page rw-page">
        <p className="review-crumb"><a className="review-crumb-link" href={href({ name: "reviewing", clientId })}>‹ {clientName}</a></p>
        <header className="review-head">
          <div className="review-head-text">
            <h1 className="review-title" ref={heading} tabIndex={-1}>Proposed week</h1>
            <p className="review-meta">
              <span>{clientName} · {shortDate(monday)} – {shortDate(addDays(monday, 6))}</span>
              <span className={`queue-chip is-${sent ? "ok" : "plain"}`}>{PROPOSAL_WORDS(data?.proposal ?? undefined)}</span>
              {dirty && <span className="queue-chip is-warn">Unsaved</span>}
            </p>
          </div>
        </header>

        {state.status === "loading" && <p className="ht-callout ours-quiet" aria-live="polite">Loading the week…</p>}
        {state.status === "error" && <div role="alert" className="ours-error"><p className="ht-body">Could not load the week.</p><p className="ht-caption">{state.message}</p></div>}

        {copy !== null && data !== null && (
          <div className="review-cols">
            <section className="review-card rw-week" aria-label="The week">
              {copy.length === 0 && <p className="review-quiet">No days in this week yet.</p>}
              {copy.map((d, di) => (
                <article key={`${d.scheduled_date}-${String(di)}`} className="rw-day" aria-label={`${weekdayOf(d.scheduled_date, monday)} ${d.coach_workouts.name}`}>
                  <header className="rw-day-head">
                    <span className="rw-day-date">{shortDate(d.scheduled_date)}</span>
                    {sent ? <h2 className="rw-day-name">{d.coach_workouts.name}</h2> : (
                      <>
                        <input className="ht-input rw-day-name-input" aria-label={`Name of ${shortDate(d.scheduled_date)}`} value={d.coach_workouts.name}
                               onChange={(e) => edit((w) => renameDay(w, di, clampWorkoutName(e.currentTarget.value)))} />
                        <label className="rw-move">
                          <span className="ht-visually-hidden">Move {d.coach_workouts.name} to</span>
                          <select value={d.scheduled_date} onChange={(e) => { const v = e.currentTarget.value; edit((w) => moveDay(w, di, v)); }}>
                            {dates.map((x) => <option key={x} value={x}>{weekdayOf(x, monday)}</option>)}
                          </select>
                        </label>
                        <button type="button" className="ht-button ht-button-quiet act-icon is-destructive" aria-label={`Remove ${d.coach_workouts.name}`} title="Remove day"
                                onClick={() => { if (window.confirm(`Remove ${d.coach_workouts.name} from the proposal?`)) edit((w) => removeDay(w, di)); }}>
                          <Icon name="trash" size={15} />
                        </button>
                      </>
                    )}
                  </header>
                  {d.coach_workouts.coach_workout_sections.map((s, si) => (
                    <div key={si} className="rw-section">
                      {s.name !== null && s.name.trim() !== "" && <h3 className="review-band-title">{s.name}</h3>}
                      {s.coach_workout_exercises.map((e, ei) => {
                        const p: ExercisePath = { day: di, section: si, exercise: ei };
                        // Keyed by what its uncontrolled fields show: a removed neighbour or a saved cue re-mounts it, never leaves a stale field.
                        return <ExerciseEditor key={`${String(ei)}:${e.id ?? ""}:${e.name}:${e.notes ?? ""}`} e={e} p={p} sent={sent} library={library} edit={edit} />;
                      })}
                      {!sent && <MovementPicker library={library} label="Add exercise" onPick={(m) => edit((w) => addExercise(w, di, si, m))} />}
                    </div>
                  ))}
                </article>
              ))}
              {!sent && (
                <div className="rw-add-day">
                  <label className="rw-move">
                    <span>Add a day on</span>
                    <select value={addOn} onChange={(e) => setAddOn(e.currentTarget.value)}>
                      {dates.map((x) => <option key={x} value={x}>{weekdayOf(x, monday)} {shortDate(x).split(" ").slice(1).join(" ")}</option>)}
                    </select>
                  </label>
                  <button type="button" className="ht-button" onClick={() => edit((w) => addDay(w, addOn, "New day"))}>Add day</button>
                </div>
              )}
            </section>

            <div className="review-side">
              <section className="review-card" aria-labelledby="rw-changes">
                <header className="review-card-head">
                  <h2 id="rw-changes" className="review-card-title">Your changes</h2>
                  {changes.length > 0 && <span className="queue-chip is-plain">{changes.length} {changes.length === 1 ? "day" : "days"}</span>}
                </header>
                {changes.length === 0 ? <p className="review-quiet">Same as written.</p> : (
                  <ul className="review-next-list rw-changes">
                    {changes.map((c) => (
                      <li key={c.date}>
                        <strong>{weekdayOf(c.date, monday)}</strong>{" "}
                        {c.movedTo !== null ? `moved to ${weekdayOf(c.movedTo, monday)}` : c.movedFrom !== null ? `moved from ${weekdayOf(c.movedFrom, monday)}`
                          : c.dayAdded ? "new day" : c.dayRemoved ? "day removed" : [
                          c.added.length > 0 ? `+ ${c.added.join(", ")}` : null,
                          c.removed.length > 0 ? `− ${c.removed.join(", ")}` : null,
                          c.changed.length > 0 ? `changed ${c.changed.join(", ")}` : null,
                        ].filter((x) => x !== null).join(" · ")}
                      </li>
                    ))}
                  </ul>
                )}
                {sent ? (
                  <>
                    {note.trim() !== "" && <p className="review-sent-text">{note}</p>}
                    <p className="review-sent-line" role="status">{PROPOSAL_SENT_FINAL}</p>
                  </>
                ) : (
                  <div className="review-compose">
                    <label className="ht-visually-hidden" htmlFor="rw-note">A note with the week</label>
                    <textarea id="rw-note" className="ht-input rw-note" placeholder="Note" maxLength={4000} rows={3}
                              value={note} onChange={(e) => { setNote(e.currentTarget.value); setDirty(true); }} />
                    {/* ONE accent control per screen (§13): the same Send asks first, then sends. */}
                    <div className="reviewer-actions" role="group" aria-label={confirming ? "Confirm send" : "Your week"}>
                      {confirming && <span className="review-quiet-line">{`Send to ${clientName.split(" ")[0] ?? "them"}? It can't be changed after.`}</span>}
                      {confirming
                        ? <button type="button" className="ht-button ht-button-quiet" onClick={() => setConfirming(false)}>Cancel</button>
                        : <button type="button" className="ht-button" disabled={busy !== null} onClick={() => write(false)}>{busy === "save" ? "Saving…" : "Save draft"}</button>}
                      <button type="button" className="ht-button ht-button-primary" disabled={busy !== null}
                              onClick={() => (confirming ? write(true) : setConfirming(true))}>
                        {busy === "send" ? "Sending…" : confirming ? "Send" : "Send week"}
                      </button>
                    </div>
                    <p className="review-quiet-line">A proposal. Their week changes only if their coach takes it.</p>
                  </div>
                )}
                {outcome !== null && <p className="review-quiet-line review-outcome" role="alert">{outcome}</p>}
              </section>
            </div>
          </div>
        )}
      </main>
    </Workspace>
  );
}

const FIELDS: { field: SetField; head: string; step: string }[] = [
  { field: "reps", head: "Reps", step: "1" },
  { field: "weight_kg", head: "kg", step: "0.5" },
  { field: "time_seconds", head: "s", step: "1" },
];
const parse = (v: string, field: SetField): number | null => {
  const t = v.trim().replace(",", ".");
  if (t === "") return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0 || n > 100000) return null;
  return field === "weight_kg" || field === "distance_m" ? Math.round(n * 100) / 100 : Math.round(n);
};

function ExerciseEditor({ e, p, sent, library, edit }: {
  e: WeekExercise;
  p: ExercisePath; sent: boolean; library: PickedMovement[]; edit: (f: (w: WeekTree) => WeekTree) => void;
}) {
  const [swapping, setSwapping] = useState(false);
  const label = (i: number, t: string | null) => (t === "warm_up" ? `W${String(i + 1)}` : String(i + 1));
  let warm = 0, work = 0;
  return (
    <div className="review-ex rw-ex">
      <div className="rw-ex-head">
        <h4 className="review-ex-name">{e.name}</h4>
        {!sent && (
          <span className="rw-ex-acts">
            <button type="button" className="ht-button ht-button-quiet" onClick={() => setSwapping((x) => !x)}>Swap</button>
            <button type="button" className="ht-button ht-button-quiet act-icon is-destructive" aria-label={`Remove ${e.name}`} title="Remove"
                    onClick={() => edit((w) => removeExercise(w, p))}><Icon name="trash" size={15} /></button>
          </span>
        )}
      </div>
      {swapping && !sent && <MovementPicker library={library} label={`Swap ${e.name} for`} autoFocus onPick={(m) => { edit((w) => swapExercise(w, p, m)); setSwapping(false); }} />}
      <div className="review-ex-body">
        <div className="review-ex-side"><VideoThumb demoVideoUrl={null} thumbnailUrl={null} size="square" /></div>
        <div className="review-sets-fit">
          <table className="review-sets rw-sets">
            <thead><tr><th scope="col">Set</th>{FIELDS.map((f) => <th key={f.field} scope="col">{f.head}</th>)}{!sent && <th scope="col"><span className="ht-visually-hidden">Remove</span></th>}</tr></thead>
            <tbody>
              {e.coach_workout_sets.map((s, si) => {
                const n = s.set_type === "warm_up" ? warm++ : work++;
                const l = label(n, s.set_type);
                return (
                  <tr key={`${String(si)}:${JSON.stringify(s)}`} className={s.set_type === "warm_up" ? "review-set is-ramp" : "review-set"}>
                    <td className="review-set-n">{l}</td>
                    {FIELDS.map((f) => (
                      <td key={f.field}>
                        {sent ? (s[f.field] ?? "–") : (
                          <input className="review-next-input rw-cell" inputMode="decimal" aria-label={`${e.name}, set ${l}, ${f.head}`}
                                 defaultValue={s[f.field] === null ? "" : String(s[f.field])} placeholder="–"
                                 onBlur={(ev) => { const v = parse(ev.currentTarget.value, f.field); if (v !== s[f.field]) edit((w) => setCell(w, p, si, f.field, v)); }} />
                        )}
                      </td>
                    ))}
                    {!sent && (
                      <td><button type="button" className="ht-button ht-button-quiet act-icon" aria-label={`Remove set ${l} of ${e.name}`} title="Remove set"
                                  disabled={e.coach_workout_sets.length <= 1} onClick={() => edit((w) => removeSet(w, p, si))}><Icon name="x" size={13} /></button></td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!sent && <button type="button" className="ht-button ht-button-quiet rw-add-set" onClick={() => edit((w) => addSet(w, p))}>Add set</button>}
        </div>
      </div>
      {sent ? (e.notes !== null && <p className="review-quiet-line">{e.notes}</p>) : (
        <textarea className="ht-input rw-ex-note" aria-label={`Cue for ${e.name}`} placeholder="Cue" rows={1} maxLength={4000}
                  defaultValue={e.notes ?? ""} onBlur={(ev) => { const v = ev.currentTarget.value; if (v !== (e.notes ?? "")) edit((w) => setNotes(w, p, v)); }} />
      )}
    </div>
  );
}

/** A movement from HIS library (the list), or a name he types (no library row): an added or swapped exercise. */
function MovementPicker({ library, label, onPick, autoFocus = false }: { library: PickedMovement[]; label: string; onPick: (m: PickedMovement) => void; autoFocus?: boolean }) {
  const [text, setText] = useState("");
  const listId = useMemo(() => `rw-lib-${Math.random().toString(36).slice(2)}`, []);
  const pick = () => {
    const t = text.trim();
    if (t === "") return;
    const hit = library.find((m) => m.name.toLowerCase() === t.toLowerCase());
    onPick(hit ?? { exercise_id: null, name: acceptExerciseName("", t), tracking_fields: null });
    setText("");
  };
  return (
    <div className="rw-picker">
      <input className="ht-input" list={listId} aria-label={label} placeholder={label} value={text} autoFocus={autoFocus}
             onChange={(e) => setText(e.currentTarget.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); pick(); } }} />
      <datalist id={listId}>{library.map((m) => <option key={m.exercise_id ?? m.name} value={m.name} />)}</datalist>
      <button type="button" className="ht-button" disabled={text.trim() === ""} onClick={pick}>Add</button>
    </div>
  );
}
